import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { HalfCircularProgressBar } from '@sorare/core/src/atoms/loader/CircularProgressIndicator/HalfCircularProgressBar/index.tsx';
import { Badge } from '@sorare/core/src/atoms/ui/Badge/index.tsx';
import { WidgetChip } from '@sorare/core/src/atoms/ui/Chip/WidgetChip/index.tsx';
import { WidgetButton } from '@sorare/core/src/atoms/ui/WidgetButton/index.tsx';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { ConnectTaskDialog } from '../ConnectTaskDialog/index.tsx';
import { IconWrapper } from '../ui.tsx';
import type {
  ConnectTaskWidget_cardSetRoot,
  ConnectTaskWidget_currentUser,
} from './__generated__/index.graphql.ts';
import request from './assets/connectIcon.webp';

type Props = {
  currentUser: ConnectTaskWidget_currentUser;
  cardSet: ConnectTaskWidget_cardSetRoot;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
};

export const ConnectTaskWidget = ({
  currentUser,
  cardSet,
  open,
  onClose,
  onOpen,
}: Props) => {
  const { inviteTasksAsReferrer } = currentUser || {};

  const { maximumInviteTasksAsReferrer } = cardSet || {};

  const typedInviteTasks = inviteTasksAsReferrer.filter(
    isTypeFn('InviteTaskAsReferrer')
  );
  return (
    <>
      <WidgetButton
        onClick={onOpen}
        chip={
          <WidgetChip>
            {inviteTasksAsReferrer.length}/{maximumInviteTasksAsReferrer}
          </WidgetChip>
        }
      >
        <Badge
          badgeContent={
            typedInviteTasks.filter(task =>
              task.steps.some(
                stepTask =>
                  stepTask.aasmState === ManagerProgressionTaskState.COMPLETED
              )
            ).length
          }
          overlap="circular"
        >
          <HalfCircularProgressBar
            size={6}
            progresses={[
              inviteTasksAsReferrer.length /
                (maximumInviteTasksAsReferrer || 1),
            ]}
            colors={[{ from: 'var(--c-set-static-surface-primary)' }]}
          />
          <IconWrapper>
            <img src={request} alt="target" width={24} height={24} />
          </IconWrapper>
        </Badge>
      </WidgetButton>

      <ConnectTaskDialog open={open} onClose={onClose} />
    </>
  );
};

ConnectTaskWidget.fragments = {
  currentUser: gql`
    fragment ConnectTaskWidget_currentUser on CurrentUser {
      slug
      inviteTasksAsReferrer(sport: $sport) {
        id
        ... on InviteTaskAsReferrer {
          id
          steps {
            id
            aasmState
          }
        }
      }
    }
  ` as TypedDocumentNode<ConnectTaskWidget_currentUser>,
  cardSetRoot: gql`
    fragment ConnectTaskWidget_cardSetRoot on CardSetRoot {
      id
      maximumInviteTasksAsReferrer(sport: $sport)
    }
  ` as TypedDocumentNode<ConnectTaskWidget_cardSetRoot>,
};
